import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "w-full max-w-xl p-8 flex flex-col space-y-4" }
const _hoisted_2 = { class: "flex flex-row items-start space-x-2 mb-3" }
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = { class: "font-semibold text-gray-900 self-center" }
const _hoisted_5 = { class: "space-y-6" }
const _hoisted_6 = { class: "flex flex-row space-x-6" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "px-5 border-l-2 text-xl border-gray-200" }, " % ", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "px-5 border-l-2 text-xl border-gray-200" }, " % ", -1)
const _hoisted_9 = { class: "flex flex-row space-x-8" }
const _hoisted_10 = { class: "px-5 border-l-2 text-xl border-gray-200" }
const _hoisted_11 = {
  key: 0,
  class: "flex flex-row space-x-8"
}
const _hoisted_12 = { class: "flex flex-col space-y-2" }
const _hoisted_13 = {
  key: 0,
  class: "pt-1 mt-1 text-sm text-gray-600 leading-4 border-t border-transparent"
}
const _hoisted_14 = { class: "w-full justify-start flex flex-col space-y-4" }
const _hoisted_15 = { class: "flex flex-end justify-end space-x-4" }

import { computed, onBeforeMount, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import axios, { CancelTokenSource } from 'axios';
import { BaseButton } from '@/lib/components/Button';
import { BaseIcon } from '@/lib/components/Icon';
import { BaseSelect } from '@/lib/components/Select';
import { BaseNumberInput } from '@/lib/components/Input';
import { MedKitRadioGroup } from '@bigpicturemedical/medkit';
import { IOption, IPredefinedTerminologyList, IValidationError } from '@/lib';
import { CopdProgram, CopdProgramSetting, FilterTag, Tag, TagGroupName } from '@/models';
import { predefinedTerminologyListFactory } from '@/lib/helpers/terminology.helper';
import { CopdProgramService, CopdTerminologyService, FilterTagService } from '@/services/api';
import { FEATURES } from '@/constants';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { useNotificationStore } from '@/stores/notification.store';

const roomAirCode = '722742002';

export default /*#__PURE__*/_defineComponent({
  __name: 'PatientSettingsModal',
  props: {
  currentSettings: {
    type: Object as PropType<CopdProgramSetting>,
    default: () => ({})
  },
  programId: {
    type: String,
    required: true
  },
  onHomeMonitoring: {
    type: Object as PropType<Tag | null>,
    default: null
  }
},
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();
const props = __props;
const emit = __emit;

const copdProgramService = new CopdProgramService();
const fetchErrors = ref({});
const request = ref<CancelTokenSource | null>(null);
const settings = ref<CopdProgramSetting>({ ...props.currentSettings });
const oxygenTypes = ref<IPredefinedTerminologyList>(predefinedTerminologyListFactory([]));
const errors = ref<IValidationError | null>(null);
const homeMonitoringOptions = ref<Array<FilterTag>>([]);
const selectedHomeMonitoringOption = ref<string>(props.onHomeMonitoring?.id || '');
const tagService = new FilterTagService();
const copdTerminologyService = new CopdTerminologyService();
const notificationStore = useNotificationStore();

const tagsFeatureFlag = computed<boolean>(() => isFeatureFlagEnabled(FEATURES.TAGS));
const validationErrors = computed(() => errors.value?.errors);
const oxygenValue = computed<string>(() => settings.value.method_of_oxygen_delivery ? settings.value.method_of_oxygen_delivery.code : '');
const showFlowRate = computed<boolean>(() => {
  const oxygenMethod = oxygenTypes.value.find(settings.value.method_of_oxygen_delivery.code);
  return !!oxygenMethod && oxygenMethod.code !== roomAirCode;
});
const homeMonitoringValues = computed<Array<IOption>>(() => homeMonitoringOptions.value.map((option: FilterTag) => ({
  value: option.id,
  label: option.name
})));
const homeMonitoringValidationError = computed(() => validationErrors.value?.home_monitoring?.[0]);

onBeforeMount(() => {
  if (tagsFeatureFlag.value) {
    fetchHomeMonitoringOptions();
  }
  fetchOxygenTypes();
});
const updateSettings = (newVal: Partial<CopdProgramSetting>) => {
  settings.value = {
    ...settings.value,
    ...newVal
  };
  Object.keys(newVal).map((key) => exposed.clearError(`settings.${key}`));
};
const clearError = (key: string) => {
  delete errors.value?.errors[key];
};
const onOxygenTypeChange = (event: string) => {
  const newOxygenType = oxygenTypes.value.find(event);
  settings.value.method_of_oxygen_delivery = newOxygenType || settings.value.method_of_oxygen_delivery;

  if (!showFlowRate.value) {
    delete settings.value.flow_rate;
  }
};
const updateHomeMonitoringOption = (option: FilterTag['id']): void => {
  selectedHomeMonitoringOption.value = option;
  exposed.clearError('home_monitoring');
};
const fetchOxygenTypes = async () => {
  try {
    const response = await copdTerminologyService.getMethodOfOxygenDelivery();
    oxygenTypes.value = predefinedTerminologyListFactory(response.data);
  } catch (e) {
    if (!axios.isAxiosError(e)) {
      throw e;
    }
    if (e.response.data) {
      if (e.response.status === 422) {
        errors.value = { errors: e.response.data.errors, message: e.response.data.message };
      } else {
        errors.value = {
          message: e.response.data.message,
          errors: {}
        };
      }
    }
  }
};
const closePatientSettingsModal = () => {
  emit('close', false);
};
const fetchHomeMonitoringOptions = async () => {
  try {
    const response = await tagService.getTagsForGroupName(TagGroupName.HomeMonitoring);
    homeMonitoringOptions.value = response.sort((a: FilterTag, b: FilterTag): number => b.name.localeCompare(a.name));
  } catch (e) {
    if (!axios.isAxiosError(e)) {
      throw e;
    }
    if (e.response.status === 422) {
      fetchErrors.value = e.response.data?.errors;
    } else {
      fetchErrors.value = {};
    }
  }
};
const updatePatientSettings = async () => {
  const copdProgramRequest: Partial<CopdProgram> = {};
  copdProgramRequest.settings = settings.value as CopdProgramSetting;

  if (tagsFeatureFlag.value) {
    copdProgramRequest.home_monitoring = selectedHomeMonitoringOption.value;
  }

  try {
    request.value = axios.CancelToken.source();

    await copdProgramService.update(
      props.programId,
      copdProgramRequest,
      { cancelToken: request.value.token }
    );

    request.value = null;
    errors.value = null;
    emit('close', true);

    notificationStore.snackbar = {
      label: t('custom.uhb.copd.patient-settings-updated')
    };
  } catch (e) {
    if (!axios.isAxiosError(e)) {
      throw e;
    }
    if (e.response.status === 422) {
      errors.value = { errors: e.response.data.errors, message: e.response.data.message };
    } else {
      errors.value = {
        message: e.response.data.message,
        errors: {}
      };
    }
  }
};
const exposed = {
  clearError
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(BaseIcon), {
          name: "lungs",
          stroke: false,
          height: "2rem"
        })
      ]),
      _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('custom.uhb.copd.patient-settings')), 1)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_unref(BaseNumberInput), {
          "model-value": settings.value.spo2_range_low?.toString() || '',
          "data-dusk": "edit-patient-settings-spo2-range-low-input",
          class: "flex w-1/2",
          step: "any",
          "show-button": false,
          label: _ctx.$t('custom.uhb.copd.spo2-target-range'),
          placeholder: "Min",
          error: validationErrors.value?.['settings.spo2_range_low']?.[0],
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (updateSettings({ spo2_range_low: +$event })))
        }, {
          "use-default": _withCtx(() => [
            _hoisted_7
          ]),
          _: 1
        }, 8, ["model-value", "label", "error"]),
        _createElementVNode("div", {
          class: _normalizeClass(["flex self-end w-1/2", { 'mt-8': validationErrors.value?.['settings.spo2_range_high']?.[0] }])
        }, [
          _createVNode(_unref(BaseNumberInput), {
            "model-value": settings.value.spo2_range_high?.toString() || '',
            "data-dusk": "edit-patient-settings-spo2-range-high-input",
            "show-button": false,
            step: "any",
            placeholder: "Max",
            error: validationErrors.value?.['settings.spo2_range_high']?.[0],
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (updateSettings({ spo2_range_high: +$event })))
          }, {
            "use-default": _withCtx(() => [
              _hoisted_8
            ]),
            _: 1
          }, 8, ["model-value", "error"])
        ], 2)
      ]),
      _createElementVNode("div", _hoisted_9, [
        (oxygenTypes.value.items.length)
          ? (_openBlock(), _createBlock(_unref(BaseSelect), {
              key: 0,
              "data-dusk": "edit-patient-settings-method-of-oxygen-delivery",
              class: _normalizeClass(showFlowRate.value ? 'w-1/2' : 'w-full'),
              "model-value": oxygenValue.value,
              label: _ctx.$t('custom.uhb.copd.inspired-oxygen'),
              placeholder: _ctx.$t('platform.common.select-option'),
              options: oxygenTypes.value.toOptions(),
              error: validationErrors.value?.['settings.method_of_oxygen_delivery']?.[0],
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => {onOxygenTypeChange($event as string); clearError('settings.method_of_oxygen_delivery')})
            }, null, 8, ["class", "model-value", "label", "placeholder", "options", "error"]))
          : _createCommentVNode("", true),
        (showFlowRate.value)
          ? (_openBlock(), _createBlock(_unref(BaseNumberInput), {
              key: 1,
              "model-value": settings.value.flow_rate ? settings.value.flow_rate.toString() : '',
              class: "w-1/2",
              step: "any",
              min: "0",
              max: "15",
              "show-button": false,
              label: _ctx.$t('custom.uhb.copd.flow-rate'),
              error: validationErrors.value?.['settings.flow_rate']?.[0],
              "data-dusk": "edit-patient-settings-flow-rate",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (updateSettings({ flow_rate: $event })))
            }, {
              "use-default": _withCtx(() => [
                _createElementVNode("div", _hoisted_10, _toDisplayString(_ctx.$t('custom.uhb.copd.litres-per-min')), 1)
              ]),
              _: 1
            }, 8, ["model-value", "label", "error"]))
          : _createCommentVNode("", true)
      ]),
      (tagsFeatureFlag.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _createVNode(_unref(MedKitRadioGroup), {
                name: "is-the-patient-home-monitoring",
                label: _ctx.$t('custom.uhb.copd.is-the-patient-home-monitoring'),
                options: homeMonitoringValues.value,
                "model-value": selectedHomeMonitoringOption.value,
                error: homeMonitoringValidationError.value,
                required: "",
                "data-dusk": "home-monitoring-options",
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (updateHomeMonitoringOption($event)))
              }, null, 8, ["label", "options", "model-value", "error"]),
              (!homeMonitoringValidationError.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_ctx.$t('custom.uhb.copd.where-patient-will-record-at-home')), 1))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_14, [
      _createElementVNode("div", _hoisted_15, [
        _createVNode(_unref(BaseButton), {
          class: "mt-8",
          size: "large",
          color: "ghost",
          onClick: closePatientSettingsModal
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('platform.common.cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(BaseButton), {
          class: "mt-8",
          size: "large",
          color: "primary",
          onClick: updatePatientSettings
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('platform.common.update')), 1)
          ]),
          _: 1
        })
      ])
    ])
  ]))
}
}

})